import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './views/LoginPage';
import DashboardPage from './views/DashboardPage';
import UsersPage from './views/UsersPage';
import UserDetailPage from './views/UserDetailPage';
import OrganizationsPage from './views/OrganizationsPage';
import OrganizationDetailPage from './views/OrganizationDetailPage';
import FeaturesPage from './views/FeaturesPage';
import NdasPage from './views/NdasPage';
import AgreementsPage from './views/AgreementsPage';
import ExportPage from './views/ExportPage';
import BulkInvoicesPage from './views/BulkInvoicesPage';
import NotificationsPage from './views/NotificationsPage';
import ApiKeysPage from './views/ApiKeysPage';
import CostReportPage from './views/CostReportPage';
import UsageHealthPage from './views/UsageHealthPage';
import PerUserUsagePage from './views/PerUserUsagePage';
import MostUsedToolsPage from './views/MostUsedToolsPage';
import BrokerAdoptionPage from './views/BrokerAdoptionPage';
import InactiveUsersPage from './views/InactiveUsersPage';
import LeaderboardPage from './views/LeaderboardPage';
import ConversionFunnelPage from './views/ConversionFunnelPage';
import ReferralSourcesPage from './views/ReferralSourcesPage';
import ConversionRequestsPage from './views/ConversionRequestsPage';
import EftPaymentsPage from './views/EftPaymentsPage';
import SubscriptionMovementsPage from './views/SubscriptionMovementsPage';
import WhatsNewPage from './views/WhatsNewPage';
import AwsHealthPage from './views/AwsHealthPage';
import FeedbackPage from './views/FeedbackPage';
import SystemFlowsPage from './views/SystemFlowsPage';
import { IS_BROKER_ADMIN, IS_CONVERSION_REQUESTS_ENABLED } from './config/admin-build';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:userId" element={<UserDetailPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="organizations/:orgId" element={<OrganizationDetailPage />} />
        <Route path="ndas" element={IS_BROKER_ADMIN ? <Navigate to="/" replace /> : <NdasPage />} />
        <Route path="agreements" element={IS_BROKER_ADMIN ? <Navigate to="/" replace /> : <AgreementsPage />} />
        <Route path="features" element={<FeaturesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        {/* Feedback (Trello #204 follow-up) — SHARED across both stacks. */}
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="cost-report" element={<CostReportPage />} />
        <Route path="usage-health" element={<UsageHealthPage />} />
        <Route path="per-user-usage" element={<PerUserUsagePage />} />
        <Route path="most-used-tools" element={<MostUsedToolsPage />} />
        <Route path="inactive-users" element={<InactiveUsersPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        {/* Broker AI Adoption — cross-stack Titan-model enablement report. Broker-admin
            ONLY (gated via SELF_SERVE_ONLY_PATHS, like the Conversion Funnel); the
            report is intentionally cross-stack (internal admins only). */}
        <Route
          path="broker-adoption"
          element={IS_BROKER_ADMIN ? <BrokerAdoptionPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="conversion-funnel"
          element={IS_BROKER_ADMIN ? <ConversionFunnelPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="referral-sources"
          element={IS_BROKER_ADMIN ? <ReferralSourcesPage /> : <Navigate to="/" replace />}
        />
        {/* EFT Payments — BOTH builds since Trello #664. Intelligent Automation
            runs the same manual-EFT channel, and the backend scopes the queue to
            the calling admin's own stack (list-eft-pending filters on the row's
            `stack`), so each build sees only its own tenants' invoices. */}
        <Route path="eft-payments" element={<EftPaymentsPage />} />
        {/* Subscription Movements (Trello #365) — broker-AI only: self-serve
            subscriptions (and so their cancellations) exist on no other stack. */}
        <Route
          path="subscription-movements"
          element={IS_BROKER_ADMIN ? <SubscriptionMovementsPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="conversion-requests"
          element={
            IS_BROKER_ADMIN && IS_CONVERSION_REQUESTS_ENABLED ? (
              <ConversionRequestsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="whats-new" element={<WhatsNewPage />} />
        {/* How It Works — internal flow-diagram docs, SHARED across both builds.
            The optional :flowKey makes each topic deep-linkable (e.g.
            /system-flows/eft-payment); the bare path defaults to the first flow. */}
        <Route path="system-flows" element={<SystemFlowsPage />} />
        <Route path="system-flows/:flowKey" element={<SystemFlowsPage />} />
        <Route
          path="aws-health"
          element={IS_BROKER_ADMIN ? <Navigate to="/" replace /> : <AwsHealthPage />}
        />
        {/* Bulk invoice preview / generate / export (Trello #363). Ungated —
            both stacks invoice their organisations. */}
        <Route path="bulk-invoices" element={<BulkInvoicesPage />} />
        <Route path="export" element={<ExportPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;

